
























































import { computed, defineComponent, onBeforeUnmount, ref } from "@vue/composition-api";
import { throttle } from "lodash-es";
import { Location } from "vue-router";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useSystemSettings from "@/hooks/useSystemSettings";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { MediaFilesController } from "@/lib/MediaFilesController";
import { getProfileUrl } from "@/lib/urlHelpers";
import { EVENTS, MoreMenuType, SideBarPosition } from "@/const";
import BaseAvatar from "@/components/BaseAvatar";
import BaseIcon from "@/components/BaseIcon";
import NavigationMore from "./components/NavigationMore";

interface MenuItem {
  name: string;
  icon: string;
  text: string;
  staticClass?: string;
  to?: Location;
  activeIcon?: string;
  hasNew?: boolean;
  exact?: boolean;
  onClick?: () => void;
  hideOnMobile?: boolean;
}

export default defineComponent({
  name: "MainNavigationBar",
  components: { NavigationMore, BaseIcon, BaseAvatar },
  setup() {
    const { authUserStrict } = useAuthUser();
    const { frontendConfig } = useSystemSettings();

    const unreadMessageThreadsCount = ref(0);
    const unreadNotificationsCount = ref(0);
    const showMore = ref(false);

    const profileTo = computed(() => getProfileUrl(authUserStrict.value));

    const avatarUrl = computed(() => {
      if (!authUserStrict.value.avatar) return "";
      return MediaFilesController.getImageThumbnail(authUserStrict.value.avatar.urlPart, 76);
    });

    const hasNewMessages = computed(() => unreadMessageThreadsCount.value > 0);
    const hasNewNotifications = computed(() => unreadNotificationsCount.value > 0);
    const isLeftPosition = computed(() => frontendConfig.value.sideBarPosition === SideBarPosition.LEFT);
    const classes = computed(() => [isLeftPosition.value ? "__left" : "__right"]);

    const menuList = computed<Array<MenuItem>>(() => {
      if (!authUserStrict.value) return [];

      return [
        {
          name: "home",
          icon: "sci-home-outline",
          activeIcon: "sci-home",
          text: $t("Home"),
          to: {
            name: "home",
          },
        },
        {
          name: "profile",
          icon: "sci-account-circle-outline",
          activeIcon: "sci-account-circle",
          text: $t("Profile"),
          to: profileTo.value,
          exact: true,
        },
        {
          name: "messages",
          icon: "sci-chat-outline",
          activeIcon: "sci-chat",
          text: $t("Messages"),
          to: {
            name: "messageThreads",
          },
          hasNew: hasNewMessages.value,
        },
        {
          name: "notifications",
          icon: "sci-bell-outline",
          activeIcon: "sci-bell",
          text: $t("Notifications"),
          to: {
            name: "notifications",
          },
          hasNew: hasNewNotifications.value,
        },
        {
          name: "more",
          icon: "sci-menu",
          text: $t("More"),
          ...(frontendConfig.value.menuType === MoreMenuType.PAGE
            ? { to: { name: "more" } }
            : { onClick: () => (showMore.value = !showMore.value) }),
        },
      ];
    });

    const getUnreadMessagesCount = async () => {
      unreadMessageThreadsCount.value = await providers.MessageThreadsProvider.getUnreadCount();
    };

    const getUnreadNotificationsCount = async () => {
      const unreadNotificationsStats = await providers.NotificationsProvider.getUnreadStats();
      if (unreadNotificationsStats) unreadNotificationsCount.value = unreadNotificationsStats.count;
    };

    const receivedMessage = throttle(getUnreadMessagesCount, 3000, { trailing: false });

    const onReceivedMessage = (message?: Message) => {
      if (message?.senderId === authUserStrict.value.id) return;
      receivedMessage();
    };
    const receivedNotification = throttle(getUnreadNotificationsCount, 3000, { trailing: false });

    const hideMenuHandler = (hide: boolean = false) => {
      const root = document.documentElement;
      root.style.setProperty("--safe-bottom-position-nav", hide ? "0px" : "56px");
    };

    const created = async () => {
      hideMenuHandler();
      await Promise.all([getUnreadMessagesCount(), getUnreadNotificationsCount()]);
    };

    created();
    emitter.on<Message>(EVENTS.MESSAGES_NEW_MESSAGE, onReceivedMessage);
    emitter.on<any>(EVENTS.MESSAGES_MARKED_READ, getUnreadMessagesCount);
    emitter.on<any>(EVENTS.NOTIFICATIONS_NEW_NOTIFICATION, receivedNotification);
    emitter.on<any>(EVENTS.NOTIFICATIONS_MARKED_READ, getUnreadNotificationsCount);
    emitter.on<any>(EVENTS.MAIN_MENU_HIDE, hideMenuHandler);

    onBeforeUnmount(() => {
      hideMenuHandler(true);
      emitter.off<any>(EVENTS.MESSAGES_NEW_MESSAGE, onReceivedMessage);
      emitter.off<any>(EVENTS.MESSAGES_MARKED_READ, getUnreadMessagesCount);
      emitter.off<any>(EVENTS.NOTIFICATIONS_NEW_NOTIFICATION, receivedNotification);
      emitter.off<any>(EVENTS.NOTIFICATIONS_MARKED_READ, getUnreadNotificationsCount);
      emitter.off<any>(EVENTS.MAIN_MENU_HIDE, hideMenuHandler);
    });

    return { avatarUrl, menuList, authUserStrict, profileTo, showMore, isLeftPosition, classes };
  },
});
